import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { IconSearch } from '@tabler/icons-react';

import DefaultModuleLayout from '../../components/layouts/DefaultModuleLayout';
import OperationsTeam from '../../components/teams/OperationsTeam';
import ProcurementTeam from '../../components/teams/ProcurementTeam';
import MyTeam from '../../components/teams/MyTeam';
import { DashboardHeader, Loader } from '../../components/common';
import { showErrorNotification } from '../../notifications';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { GET_PROCUREMENT_HIERARCHY } from '../../apollo/queries/procurementHierarchy';
import DebouncedInput from '../../components/common/DebouncedInput';
import { searchHierarchy } from '../../helpers/filterNestedArrays';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import TeamsModal from '../../components/teams/TeamsModal';
import { UserSidesheet } from '../../components/users';
import OrganisationSearchContext from '../../context/OrganisationSearchContext';

function TeamsPage() {
    const { authUser } = useAuth();
    const isUserDivisionalManager = checkUserRole(authUser, [
        UserRole.DIVISIONAL_MANAGER,
    ]);

    const [organisationSearch, setOrganisationSearch] = useState('');

    const {
        data: operationsHierarchy,
        loading: loadingOperationsHierarchy,
        error: operationsHierarchyError,
    } = useQuery(GET_OPS_HIERARCHY, {
        variables: {
            includeClosedStores: false,
            filterEmptyHierarchies: true,
        },
    });

    const {
        data: procurementHierarchy,
        loading: loadingProcurementHierarchy,
        error: procurementHierarchyError,
    } = useQuery(GET_PROCUREMENT_HIERARCHY);

    const loading = useMemo(
        () => loadingOperationsHierarchy || loadingProcurementHierarchy,
        [loadingOperationsHierarchy, loadingProcurementHierarchy],
    );

    const opsHierarchy = useMemo(() => {
        if (operationsHierarchy) {
            const { director, seniorExecutive, opsAreas } =
                operationsHierarchy.getOperationsHierarchy;
            const filteredOpsAreas = searchHierarchy(
                opsAreas,
                organisationSearch,
            );

            return {
                director,
                seniorExecutive,
                opsAreas: filteredOpsAreas,
            };
        }

        return {
            director: null,
            seniorExecutive: null,
            opsAreas: [],
        };
    }, [operationsHierarchy, organisationSearch]);

    const procureHierarchy = useMemo(() => {
        const { executives, director } =
            procurementHierarchy?.getProcurementHierarchy ?? {};

        if (procurementHierarchy) {
            const filteredProcurementHierarchy = searchHierarchy(
                executives,
                organisationSearch,
            );

            return {
                director,
                executives: filteredProcurementHierarchy,
            };
        }
        return {
            director: null,
            executives: [],
        };
    }, [procurementHierarchy]);

    useEffect(() => {
        if (operationsHierarchyError) {
            showErrorNotification(operationsHierarchyError.message);
        }
        if (procurementHierarchyError) {
            showErrorNotification(procurementHierarchyError.message);
        }
    }, [operationsHierarchyError, procurementHierarchyError]);

    useEffect(() => {
        localStorage.setItem('selectedModule', 'Organisation Hierarchy');
    }, []);

    const organisationSearchValue = useMemo(() => {
        return { organisationSearch };
    }, [organisationSearch]);

    return (
        <OrganisationSearchContext.Provider value={organisationSearchValue}>
            <DefaultModuleLayout>
                <div className="w-full bg-light-grey overflow-x-hidden overflow-y-scroll h-[calc(100vh-64px)]">
                    <div className="relative">
                        <DashboardHeader
                            title="Organisation Hierarchy"
                            hasBreadcrumb={false}
                            hasDate
                            rightComponent={
                                <DebouncedInput
                                    leftSection={<IconSearch />}
                                    placeholder="Search Organisation"
                                    value={organisationSearch}
                                    onChange={event =>
                                        setOrganisationSearch(
                                            event.target.value,
                                        )
                                    }
                                    className="py-2 w-[400px] self-end"
                                />
                            }
                        />

                        {loading ? (
                            <Loader isPageLoader />
                        ) : (
                            <Tabs
                                className="px-11 w-full mb-8 "
                                defaultValue={
                                    isUserDivisionalManager
                                        ? 'My Team'
                                        : 'Operations Team'
                                }
                            >
                                <div className="sticky top-[96px] z-20 bg-[#f8f9fa] w-full">
                                    <Tabs.List className="">
                                        {isUserDivisionalManager && (
                                            <Tabs.Tab value="My Team">
                                                My Team
                                            </Tabs.Tab>
                                        )}
                                        <Tabs.Tab value="Operations Team">
                                            Operations Team
                                        </Tabs.Tab>
                                        <Tabs.Tab value="Procurement Team">
                                            Procurement Team
                                        </Tabs.Tab>
                                    </Tabs.List>
                                </div>

                                <Tabs.Panel value="Operations Team">
                                    {loadingOperationsHierarchy ? (
                                        <Loader isPageLoader />
                                    ) : (
                                        <OperationsTeam
                                            seniorExecutive={
                                                opsHierarchy?.seniorExecutive
                                            }
                                            director={opsHierarchy?.director}
                                            opsAreas={opsHierarchy?.opsAreas}
                                        />
                                    )}
                                </Tabs.Panel>

                                <Tabs.Panel value="Procurement Team">
                                    <ProcurementTeam
                                        director={procureHierarchy?.director}
                                        executives={
                                            procureHierarchy?.executives ?? []
                                        }
                                    />
                                </Tabs.Panel>
                                <Tabs.Panel value="My Team">
                                    <MyTeam />
                                </Tabs.Panel>
                            </Tabs>
                        )}
                    </div>
                </div>
            </DefaultModuleLayout>
            <UserSidesheet />
            <TeamsModal executives={procureHierarchy?.executives ?? []} />
        </OrganisationSearchContext.Provider>
    );
}

export default TeamsPage;
